// # 창 전체 단축키
//
// 조합은 슬랙 도움말 "Slack keyboard shortcuts"(https://slack.com/help/articles/201374536)의
// 맥 표기를 따른다. `mod` 는 맥의 ⌘, 그 밖의 Ctrl. 여기는 **판정만** 한다 — 무엇을 할지는
// `workspace.tsx` 가 정한다. 서식 단축키는 입력칸 안의 일이라 `@maru/protocol` 의 `format_of_key`.

import type { ChannelId } from '@maru/protocol'

export type Nav =
  | 'switch'
  | 'shortcuts'
  | 'back'
  | 'forward'
  | 'prev_channel'
  | 'next_channel'
  | 'prev_unread'
  | 'next_unread'
  | 'unreads'
  | 'threads'
  | 'activity'
  | 'saved'
  | 'settings'

export interface Key {
  key: string
  mod: boolean
  shift: boolean
  alt: boolean
}

const WITH_MOD: Readonly<Record<string, Nav>> = {
  k: 'switch',
  '/': 'shortcuts',
  '[': 'back',
  ']': 'forward',
  ',': 'settings',
}

/** ⇧⌘ 조합 — 슬랙과 같은 자리. ⌘A(전체 선택)·⌘T·⌘M·⌘S 는 입력칸·브라우저의 것이라 ⇧ 가 붙어야 판을 연다. */
const WITH_SHIFT_MOD: Readonly<Record<string, Nav>> = {
  a: 'unreads',
  t: 'threads',
  m: 'activity',
  s: 'saved',
}

/** 키 → 창 동작. 모르는 조합이면 `null` — 입력칸이 제 일을 하게 둔다. */
export function nav_of_key(k: Key): Nav | null {
  const shifted = k.mod && k.shift && !k.alt ? WITH_SHIFT_MOD[k.key.toLowerCase()] : undefined
  if (shifted !== undefined) return shifted
  if (k.mod && !k.alt) return WITH_MOD[k.key.toLowerCase()] ?? null
  if (!k.alt || k.mod) return null
  if (k.key === 'ArrowUp') return k.shift ? 'prev_unread' : 'prev_channel'
  if (k.key === 'ArrowDown') return k.shift ? 'next_unread' : 'next_channel'
  return null
}

/**
 * 사이드바 순서로 한 칸 옮긴다. `only` 가 있으면 그 조건(안 읽음)을 만족하는 채널만 센다.
 * 끝에서 끝으로 돌아간다. 갈 곳이 없으면 `null`.
 */
export function step_channel(
  order: readonly ChannelId[],
  open: ChannelId | null,
  dir: 1 | -1,
  only?: (id: ChannelId) => boolean,
): ChannelId | null {
  const n = order.length
  if (n === 0) return null
  const at = open === null ? -1 : order.indexOf(open)
  for (let i = 1; i <= n; i += 1) {
    const idx = (((at + dir * i) % n) + n) % n
    const id = order[idx] as ChannelId
    if (id === open) continue
    if (only === undefined || only(id)) return id
  }
  return null
}

/**
 * 채널 방문 기록(⌘[ ⌘]). 브라우저 뒤로·앞으로와 같다 — 뒤로 간 뒤 새로 가면 앞쪽은 버린다.
 * 기록을 따라 옮길 때는 `visit` 을 부르지 않는다(부르는 쪽이 `moving` 으로 가린다).
 */
export class History {
  #list: ChannelId[] = []
  #at = -1
  static readonly MAX = 50

  visit(id: ChannelId): void {
    if (this.#list[this.#at] === id) return
    this.#list = this.#list.slice(0, this.#at + 1)
    this.#list.push(id)
    if (this.#list.length > History.MAX) this.#list.shift()
    this.#at = this.#list.length - 1
  }

  back(): ChannelId | null {
    if (this.#at <= 0) return null
    this.#at -= 1
    return this.#list[this.#at] ?? null
  }

  forward(): ChannelId | null {
    if (this.#at >= this.#list.length - 1) return null
    this.#at += 1
    return this.#list[this.#at] ?? null
  }
}
